/**
 * Hook für Status-Filterung von Flurstücken und Eigentümern.
 * Kapselt die Logik zum Filtern nach Verhandlungs-Status (aus den Diagrammen)
 * und das automatische Zurücksetzen bei Klick außerhalb.
 */
import { useState, useEffect, useCallback, RefObject } from 'react';
import { apiService } from '../../../../services/api';
import { useToast } from '../../../../contexts/ToastContext';

interface UseStatusFilterProps {
  projectId: string;
  areaId?: string;
  /** Alle Flurstücke (ungefiltert) */
  allParcels: any[];
  /** Alle Eigentümer (ungefiltert) */
  allOwners: any[];
  /** Default-Flurstücke (z.B. nach Owner-Selektion gefiltert) */
  defaultParcels: any[];
  /** Default-Owners (z.B. nach Parcel-Selektion gefiltert) */
  defaultOwners: any[];
  /** Ref auf MapSection-Container zum Scroll-to */
  mapSectionRef: RefObject<HTMLDivElement | null>;
  /** Callback zum Zurücksetzen der Parcel-/Owner-Selektion */
  onClearSelection: () => void;
}

export function useStatusFilter({
  projectId,
  areaId,
  allParcels,
  allOwners,
  defaultParcels,
  defaultOwners,
  mapSectionRef,
  onClearSelection,
}: UseStatusFilterProps) {
  const { showError } = useToast();

  const [selectedStatusFilter, setSelectedStatusFilter] = useState<{
    type: 'parcel' | 'owner';
    status: string;
  } | null>(null);
  const [filteredParcels, setFilteredParcels] = useState<any[]>([]);
  const [filteredOwners, setFilteredOwners] = useState<any[]>([]);

  const clearFilter = useCallback(() => {
    setSelectedStatusFilter(null);
    setFilteredParcels([]);
    setFilteredOwners([]);
  }, []);

  const scrollToMap = useCallback(() => {
    setTimeout(() => {
      if (mapSectionRef.current) {
        const pos = mapSectionRef.current.getBoundingClientRect().top;
        window.scrollTo({
          top: pos + window.pageYOffset - 20,
          behavior: 'smooth',
        });
      }
    }, 100);
  }, [mapSectionRef]);

  const handleParcelStatusClick = useCallback(async (status: string) => {
    try {
      setSelectedStatusFilter({ type: 'parcel', status });

      const filtered = allParcels.filter(p => (p.status || 'Kein Kontakt') === status);
      setFilteredParcels(filtered);

      if (filtered.length > 0 && areaId) {
        const parcelIds = filtered.map(p => p.id);
        const owners = await apiService.getOwnersForParcels(projectId, parcelIds);
        setFilteredOwners(owners);
      } else {
        setFilteredOwners([]);
      }

      onClearSelection();
      scrollToMap();
    } catch (error: any) {
      showError('Fehler beim Filtern');
    }
  }, [allParcels, areaId, projectId, onClearSelection, scrollToMap, showError]);

  const handleOwnerStatusClick = useCallback(async (status: string) => {
    try {
      setSelectedStatusFilter({ type: 'owner', status });

      const filtered = allOwners.filter(o => (o.status || 'Kein Kontakt') === status);
      setFilteredOwners(filtered);

      if (filtered.length > 0 && areaId) {
        const ownerIds = filtered.map(o => o.id);
        const parcels = await apiService.getParcelsForOwners(projectId, areaId, ownerIds);
        setFilteredParcels(parcels);
      } else {
        setFilteredParcels([]);
      }

      onClearSelection();
      scrollToMap();
    } catch (error: any) {
      showError('Fehler beim Filtern');
    }
  }, [allOwners, areaId, projectId, onClearSelection, scrollToMap, showError]);

  /** Klick außerhalb von Sections/Charts setzt den Filter zurück */
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!selectedStatusFilter) return;

      const target = event.target as HTMLElement;
      const isClickable = target.closest('svg') || target.closest('[class*="MuiPaper"]');

      if (!isClickable) {
        clearFilter();
        onClearSelection();
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, [selectedStatusFilter, clearFilter, onClearSelection]);

  const displayedParcels = selectedStatusFilter ? filteredParcels : defaultParcels;

  const displayedOwners = selectedStatusFilter
    ? filteredOwners
    : defaultOwners;

  return {
    selectedStatusFilter,
    displayedParcels,
    displayedOwners,
    handleParcelStatusClick,
    handleOwnerStatusClick,
    clearFilter,
  };
}
